import { createContext, useContext, useEffect, useState, useCallback } from "react";

const ConsentContext = createContext(null);
const STORAGE_KEY = "fc_consent";

function startPostHog() {
  const ph = window.__FC_POSTHOG__;
  if (!ph || !window.posthog || window.__fc_ph_started) return;
  try {
    window.posthog.init(ph.key, ph.config);
    window.__fc_ph_started = true;
  } catch (e) {
    /* noop */
  }
}

function stopPostHog() {
  if (window.__fc_ph_started && window.posthog) {
    try {
      window.posthog.opt_out_capturing && window.posthog.opt_out_capturing();
      window.posthog.stopSessionRecording && window.posthog.stopSessionRecording();
    } catch (e) {
      /* noop */
    }
  }
}

export function ConsentProvider({ children }) {
  const [consent, setConsent] = useState(null); // {decided, analytics}
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    let stored = null;
    try {
      stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    } catch (e) {
      stored = null;
    }
    if (stored && typeof stored.analytics === "boolean") {
      setConsent(stored);
      if (stored.analytics) startPostHog();
    } else {
      setConsent({ decided: false, analytics: false });
    }
  }, []);

  const persist = useCallback((next) => {
    const value = { ...next, decided: true, timestamp: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    setConsent(value);
    if (value.analytics) {
      startPostHog();
    } else {
      stopPostHog();
    }
    return value;
  }, []);

  const acceptAll = useCallback(() => {
    persist({ analytics: true });
    setSettingsOpen(false);
  }, [persist]);

  const rejectAll = useCallback(() => {
    const wasOn = window.__fc_ph_started;
    persist({ analytics: false });
    setSettingsOpen(false);
    if (wasOn) window.location.reload();
  }, [persist]);

  const savePreferences = useCallback((analytics) => {
    const wasOn = window.__fc_ph_started;
    persist({ analytics });
    setSettingsOpen(false);
    if (wasOn && !analytics) window.location.reload();
  }, [persist]);

  const openSettings = useCallback(() => setSettingsOpen(true), []);
  const closeSettings = useCallback(() => setSettingsOpen(false), []);

  return (
    <ConsentContext.Provider
      value={{ consent, settingsOpen, acceptAll, rejectAll, savePreferences, openSettings, closeSettings }}
    >
      {children}
    </ConsentContext.Provider>
  );
}

export const useConsent = () => useContext(ConsentContext);
